import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Brain, Eye, Activity, Clock, DollarSign, Zap } from 'lucide-react';

interface AnalysisResult {
  analysis: string;
  tokens_used: number;
  response_time_ms: number;
  analysis_type: string;
  frames_analyzed: number;
  pose_frames_analyzed?: number;
  error?: string;
}

interface AnalysisComparisonGridProps {
  poseResult: AnalysisResult | null;
  videoResult: AnalysisResult | null;
  combinedResult: AnalysisResult | null;
  isLoading: { pose: boolean; video: boolean; combined: boolean };
}

export function AnalysisComparisonGrid({ 
  poseResult, 
  videoResult, 
  combinedResult, 
  isLoading 
}: AnalysisComparisonGridProps) {
  const getIcon = (type: string) => {
    switch (type) {
      case 'pose_only': return <Activity className="h-5 w-5" />;
      case 'video_only': return <Eye className="h-5 w-5" />;
      case 'video_and_pose': return <Brain className="h-5 w-5" />;
      default: return <Brain className="h-5 w-5" />;
    }
  };

  const getTitle = (type: string) => {
    switch (type) {
      case 'pose_only': return 'Pose Data Only';
      case 'video_only': return 'Video Only';
      case 'video_and_pose': return 'Video + Pose';
      default: return 'Analysis';
    }
  };

  const estimateCost = (tokens: number) => {
    // Rough GPT-4o pricing: $5/1M input tokens, $15/1M output tokens
    // Assuming 70% input, 30% output for average
    const inputTokens = Math.floor(tokens * 0.7);
    const outputTokens = Math.floor(tokens * 0.3);
    const cost = (inputTokens * 5 + outputTokens * 15) / 1000000;
    return `$${cost.toFixed(4)}`;
  };

  const AnalysisCard = ({ result, loading, type }: { 
    result: AnalysisResult | null; 
    loading: boolean; 
    type: string;
  }) => (
    <Card className="flex-1">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          {getIcon(type)}
          {getTitle(type)}
          {result && (
            <Badge variant="outline" className="ml-auto">
              {result.analysis_type}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-full"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        ) : result?.error ? (
          <div className="text-destructive text-sm">
            Error: {result.error}
          </div>
        ) : result ? (
          <>
            {/* Analysis content */}
            <div className="text-sm leading-relaxed">
              {result.analysis.split('\n').map((line, idx) => (
                <p key={idx} className="mb-2">{line}</p>
              ))}
            </div>
            
            <Separator />
            
            {/* Metrics */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="flex items-center gap-1">
                <Zap className="h-3 w-3 text-blue-500" />
                <span className="text-muted-foreground">Tokens:</span>
                <span className="font-medium">{result.tokens_used}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-green-500" />
                <span className="text-muted-foreground">Time:</span>
                <span className="font-medium">{result.response_time_ms}ms</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="h-3 w-3 text-yellow-500" />
                <span className="text-muted-foreground">Est. Cost:</span>
                <span className="font-medium">{estimateCost(result.tokens_used)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Activity className="h-3 w-3 text-purple-500" />
                <span className="text-muted-foreground">Frames:</span>
                <span className="font-medium">
                  {result.pose_frames_analyzed 
                    ? `${result.frames_analyzed}v + ${result.pose_frames_analyzed}p`
                    : result.frames_analyzed
                  }
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="text-muted-foreground text-sm text-center py-8">
            No analysis yet
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Comparison Grid */}
      <div className="flex gap-4">
        <AnalysisCard 
          result={poseResult} 
          loading={isLoading.pose} 
          type="pose_only" 
        />
        <AnalysisCard 
          result={videoResult} 
          loading={isLoading.video} 
          type="video_only" 
        />
        <AnalysisCard 
          result={combinedResult} 
          loading={isLoading.combined} 
          type="video_and_pose" 
        />
      </div>

      {/* Summary Comparison */}
      {(poseResult || videoResult || combinedResult) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Performance Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div className="font-medium">Method</div>
              <div className="font-medium">Tokens</div>
              <div className="font-medium">Time (ms)</div>
              <div className="font-medium">Est. Cost</div>
              
              {poseResult && (
                <>
                  <div>Pose Only</div>
                  <div>{poseResult.tokens_used}</div>
                  <div>{poseResult.response_time_ms}</div>
                  <div>{estimateCost(poseResult.tokens_used)}</div>
                </>
              )}
              
              {videoResult && (
                <>
                  <div>Video Only</div>
                  <div>{videoResult.tokens_used}</div>
                  <div>{videoResult.response_time_ms}</div>
                  <div>{estimateCost(videoResult.tokens_used)}</div>
                </>
              )}
              
              {combinedResult && (
                <>
                  <div>Video + Pose</div>
                  <div>{combinedResult.tokens_used}</div>
                  <div>{combinedResult.response_time_ms}</div>
                  <div>{estimateCost(combinedResult.tokens_used)}</div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}